<script setup>
import { onMounted, reactive, ref, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import http from '@/api/http'
import { useAuthStore } from '@/stores/auth'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Select from 'primevue/select'
import Message from 'primevue/message'
import ToggleSwitch from 'primevue/toggleswitch'

const toast = useToast()
const confirm = useConfirm()
const auth = useAuthStore()

const rows = ref([])
const loading = ref(false)
const error = ref('')

const filters = reactive({
  role: null,
  onlyActive: false,
})

const roleOptions = [
  { label: 'Все роли', value: null },
  { label: 'Администратор', value: 'admin' },
  { label: 'Оператор', value: 'operator' },
]

const editableRoleOptions = [
  { label: 'Администратор', value: 'admin' },
  { label: 'Оператор', value: 'operator' },
]

const showCreate = ref(false)
const createForm = reactive({
  email: '',
  password: '',
  name: '',
  role: 'operator',
})
const creating = ref(false)
const createError = ref('')

const showEdit = ref(false)
const editForm = reactive({
  id: null,
  email: '',
  name: '',
  role: 'operator',
  is_active: true,
})
const editing = ref(false)
const editError = ref('')

const showPassword = ref(false)
const passwordForm = reactive({
  id: null,
  email: '',
  password: '',
})
const settingPassword = ref(false)
const passwordError = ref('')

const myId = computed(() => auth.user?.id ?? null)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = {}
    if (filters.role) params.role = filters.role
    if (filters.onlyActive) params.only_active = 1
    const { data } = await http.get('/users', { params })
    rows.value = data?.items ?? []
  } catch (e) {
    error.value = e?.response?.data?.error?.message || 'Не удалось загрузить пользователей'
  } finally {
    loading.value = false
  }
}

function openCreate() {
  createForm.email = ''
  createForm.password = ''
  createForm.name = ''
  createForm.role = 'operator'
  createError.value = ''
  showCreate.value = true
}

async function submitCreate() {
  createError.value = ''
  if (!createForm.email || !createForm.password) {
    createError.value = 'Заполните email и пароль'
    return
  }
  creating.value = true
  try {
    await http.post('/users', { ...createForm })
    toast.add({ severity: 'success', summary: 'Создан', life: 2500 })
    showCreate.value = false
    await load()
  } catch (e) {
    createError.value = e?.response?.data?.error?.message || 'Не удалось создать'
  } finally {
    creating.value = false
  }
}

function openEdit(row) {
  editForm.id = row.id
  editForm.email = row.email
  editForm.name = row.name ?? ''
  editForm.role = row.role
  editForm.is_active = row.is_active
  editError.value = ''
  showEdit.value = true
}

async function submitEdit() {
  editError.value = ''
  editing.value = true
  try {
    await http.patch(`/users/${editForm.id}`, {
      name: editForm.name,
      role: editForm.role,
      is_active: editForm.is_active,
    })
    toast.add({ severity: 'success', summary: 'Сохранено', life: 2500 })
    showEdit.value = false
    await load()
  } catch (e) {
    editError.value = e?.response?.data?.error?.message || 'Не удалось сохранить'
  } finally {
    editing.value = false
  }
}

function openPassword(row) {
  passwordForm.id = row.id
  passwordForm.email = row.email
  passwordForm.password = ''
  passwordError.value = ''
  showPassword.value = true
}

async function submitPassword() {
  passwordError.value = ''
  if (!passwordForm.password || passwordForm.password.length < 8) {
    passwordError.value = 'Минимум 8 символов'
    return
  }
  settingPassword.value = true
  try {
    await http.post(`/users/${passwordForm.id}/password`, { password: passwordForm.password })
    toast.add({ severity: 'success', summary: 'Пароль обновлён', life: 2500 })
    showPassword.value = false
  } catch (e) {
    passwordError.value = e?.response?.data?.error?.message || 'Ошибка'
  } finally {
    settingPassword.value = false
  }
}

function deactivate(row) {
  confirm.require({
    message: `Деактивировать пользователя «${row.email}»? Он не сможет входить.`,
    header: 'Подтверждение',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Отмена',
    acceptLabel: 'Деактивировать',
    acceptProps: { severity: 'danger' },
    accept: async () => {
      try {
        await http.delete(`/users/${row.id}`)
        toast.add({ severity: 'success', summary: 'Деактивирован', life: 2500 })
        await load()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: e?.response?.data?.error?.message || 'Не удалось',
          life: 4000,
        })
      }
    },
  })
}

async function activate(row) {
  try {
    await http.patch(`/users/${row.id}`, { is_active: true })
    toast.add({ severity: 'success', summary: 'Активирован', life: 2500 })
    await load()
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: e?.response?.data?.error?.message || 'Не удалось',
      life: 4000,
    })
  }
}

function fmtDate(s) {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('ru-RU')
  } catch (_) {
    return s
  }
}

onMounted(load)
</script>

<template>
  <div class="page">
    <div class="toolbar">
      <Select
        v-model="filters.role"
        :options="roleOptions"
        option-label="label"
        option-value="value"
        @change="load"
      />
      <div class="toolbar__switch">
        <ToggleSwitch
          v-model="filters.onlyActive"
          input-id="onlyActiveUsers"
          @change="load"
        />
        <label for="onlyActiveUsers">Только активные</label>
      </div>

      <div class="toolbar__spacer" />

      <Button
        icon="pi pi-refresh"
        label="Обновить"
        severity="secondary"
        outlined
        :loading="loading"
        @click="load"
      />
      <Button icon="pi pi-user-plus" label="Создать" @click="openCreate" />
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <DataTable :value="rows" :loading="loading" data-key="id" striped-rows>
      <Column field="id" header="#" style="width: 5rem" />
      <Column field="email" header="E-mail" />
      <Column header="Имя">
        <template #body="{ data }">{{ data.name || '—' }}</template>
      </Column>
      <Column header="Роль" style="width: 9rem">
        <template #body="{ data }">
          <Tag
            :severity="data.role === 'admin' ? 'warn' : 'info'"
            :value="data.role === 'admin' ? 'admin' : 'operator'"
          />
        </template>
      </Column>
      <Column header="Статус" style="width: 8rem">
        <template #body="{ data }">
          <Tag
            v-if="data.is_active"
            value="активен"
            severity="success"
          />
          <Tag v-else value="отключён" severity="secondary" />
        </template>
      </Column>
      <Column header="Создан" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.date_create) }}</template>
      </Column>
      <Column header="Последний вход" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.last_login) }}</template>
      </Column>
      <Column header="" style="width: 11rem">
        <template #body="{ data }">
          <div class="actions">
            <Button
              text
              rounded
              icon="pi pi-pencil"
              aria-label="Редактировать"
              @click="openEdit(data)"
            />
            <Button
              text
              rounded
              icon="pi pi-key"
              aria-label="Сменить пароль"
              @click="openPassword(data)"
            />
            <Button
              v-if="data.is_active && data.id !== myId"
              text
              rounded
              severity="danger"
              icon="pi pi-ban"
              aria-label="Деактивировать"
              @click="deactivate(data)"
            />
            <Button
              v-else-if="!data.is_active"
              text
              rounded
              severity="success"
              icon="pi pi-check-circle"
              aria-label="Активировать"
              @click="activate(data)"
            />
          </div>
        </template>
      </Column>
      <template #empty>
        <p class="muted">Пользователей нет</p>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showCreate"
      modal
      header="Новый пользователь"
      :style="{ width: '32rem' }"
      :closable="!creating"
    >
      <div class="dialog-body">
        <div class="row">
          <label>E-mail</label>
          <InputText v-model="createForm.email" type="email" autofocus fluid />
        </div>
        <div class="row">
          <label>Имя</label>
          <InputText v-model="createForm.name" placeholder="опционально" fluid />
        </div>
        <div class="row">
          <label>Пароль (мин. 8 символов)</label>
          <Password v-model="createForm.password" :feedback="false" toggle-mask fluid input-class="w-full" />
        </div>
        <div class="row">
          <label>Роль</label>
          <Select
            v-model="createForm.role"
            :options="editableRoleOptions"
            option-label="label"
            option-value="value"
            fluid
          />
        </div>
        <Message v-if="createError" severity="error" :closable="false">{{ createError }}</Message>
      </div>
      <template #footer>
        <Button label="Отмена" text @click="showCreate = false" :disabled="creating" />
        <Button label="Создать" icon="pi pi-check" :loading="creating" @click="submitCreate" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showEdit"
      modal
      :header="`Редактирование: ${editForm.email}`"
      :style="{ width: '32rem' }"
      :closable="!editing"
    >
      <div class="dialog-body">
        <div class="row">
          <label>Имя</label>
          <InputText v-model="editForm.name" fluid />
        </div>
        <div class="row">
          <label>Роль</label>
          <Select
            v-model="editForm.role"
            :options="editableRoleOptions"
            option-label="label"
            option-value="value"
            :disabled="editForm.id === myId"
            fluid
          />
          <small v-if="editForm.id === myId" class="muted">Свою роль изменить нельзя.</small>
        </div>
        <div class="row toggle-row">
          <ToggleSwitch
            v-model="editForm.is_active"
            input-id="editActive"
            :disabled="editForm.id === myId"
          />
          <label for="editActive">Активен</label>
          <small v-if="editForm.id === myId" class="muted">(нельзя отключить себя)</small>
        </div>
        <Message v-if="editError" severity="error" :closable="false">{{ editError }}</Message>
      </div>
      <template #footer>
        <Button label="Отмена" text @click="showEdit = false" :disabled="editing" />
        <Button label="Сохранить" icon="pi pi-check" :loading="editing" @click="submitEdit" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showPassword"
      modal
      :header="`Новый пароль: ${passwordForm.email}`"
      :style="{ width: '28rem' }"
      :closable="!settingPassword"
    >
      <div class="dialog-body">
        <Password
          v-model="passwordForm.password"
          :feedback="false"
          toggle-mask
          fluid
          input-class="w-full"
          autofocus
        />
        <Message v-if="passwordError" severity="error" :closable="false">{{ passwordError }}</Message>
        <small class="muted">Минимум 8 символов. Сообщите его пользователю — рекомендуется поменять при следующем входе.</small>
      </div>
      <template #footer>
        <Button label="Отмена" text @click="showPassword = false" :disabled="settingPassword" />
        <Button label="Сохранить" icon="pi pi-check" :loading="settingPassword" @click="submitPassword" />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.toolbar__switch {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.toolbar__spacer { flex: 1; }
.error { color: #b91c1c; margin: 0 0 0.75rem; }
.actions { display: flex; gap: 0.125rem; }

.dialog-body { display: flex; flex-direction: column; gap: 0.75rem; }
.row { display: flex; flex-direction: column; gap: 0.375rem; }
.row > label { font-size: 0.85rem; color: #4b5563; }
.toggle-row {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

:deep(.p-password) { width: 100%; }
:deep(.p-password input) { width: 100%; }
</style>
